import { useCallback, useEffect, useRef, useState } from "react";

import type { OwnAgentSnapshot } from "~/lib/autocomplete-providers";
import { CHANNEL_TYPE_EVENT } from "~/lib/channel-type";
import { ownAgentUserId } from "~/lib/own-agent";
import type { Client } from "~/lib/matrix";

export interface OwnAgentScope {
  ownerMxid: string;
  roomId: string;
  scopeId: string;
}

export interface OwnAgentState {
  ready: boolean;
  agentMxid: string | null;
  handle: string | null;
  scope: OwnAgentScope | null;
  guard: (expected: OwnAgentScope) => boolean;
}

interface MemberContent {
  membership?: unknown;
}

type ChannelContent = { type?: unknown };

function isValidLocalPart(userId: string): boolean {
  return userId.startsWith("@") && userId.length > 1 && userId.indexOf(":") > 0;
}

function localPart(ownerMxid: string): string {
  const colon = ownerMxid.indexOf(":");
  return ownerMxid.slice(1, colon < 0 ? undefined : colon);
}

function handleForOwner(ownerMxid: string): string {
  return `@${localPart(ownerMxid)}_agent`;
}

function isJoin(content: unknown): boolean {
  if (!content || typeof content !== "object") return false;
  return (content as MemberContent).membership === "join";
}

function channelAllowsAgents(content: unknown): boolean {
  if (!content || typeof content !== "object") return false;
  const type = (content as ChannelContent).type;
  return type === "mixed" || type === "agent-only";
}

function makeNonce(): string {
  return Math.random().toString(36).slice(2, 12);
}

export const OWN_AGENT_CHANGED_COPY =
  "Your agent changed. Remove the mention and select your agent again.";
export const OWN_AGENT_UNAVAILABLE_COPY = "Your agent is not available in this room";
export const SCHEDULING_NOT_SUPPORTED_COPY =
  "Send this mention now; scheduling agent mentions is not supported.";

export function buildOwnAgentSnapshot(state: OwnAgentState): OwnAgentSnapshot | null {
  if (!state.agentMxid || !state.handle) return null;
  return {
    ready: state.scope !== null,
    ownerMxid: state.scope?.ownerMxid ?? "",
    roomId: state.scope?.roomId ?? "",
    scopeId: state.scope?.scopeId ?? "",
    agentMxid: state.agentMxid,
    handle: state.handle,
  };
}

export function useOwnAgent(
  client: Client | null,
  roomId: string,
  userId: string | null,
): OwnAgentState {
  const [, setTick] = useState(0);
  const nonceRef = useRef("");
  const rotationsRef = useRef(0);
  const scopeIdRef = useRef("");
  const ownerMemberRef = useRef<unknown>(null);
  const agentMemberRef = useRef<unknown>(null);
  const channelRef = useRef<unknown>(null);
  const prevReadyRef = useRef(false);

  const authedId = client?.bindings.getUserId() ?? null;
  const ownerValid = userId !== null && isValidLocalPart(userId) && userId === authedId;
  const agentMxid = ownerValid ? ownAgentUserId(userId as string) : null;
  const handle = ownerValid ? handleForOwner(userId as string) : null;

  const applyReadiness = useCallback(() => {
    const ownerJoin = isJoin(ownerMemberRef.current);
    const agentJoin = isJoin(agentMemberRef.current);
    const ready = ownerValid && agentJoin && ownerJoin && channelAllowsAgents(channelRef.current);
    if (prevReadyRef.current && !ready) {
      rotationsRef.current += 1;
      scopeIdRef.current = `${nonceRef.current}-${rotationsRef.current}`;
    }
    prevReadyRef.current = ready;
    setTick((v) => v + 1);
  }, [ownerValid]);

  useEffect(() => {
    nonceRef.current = makeNonce();
    rotationsRef.current = 0;
    scopeIdRef.current = `${nonceRef.current}-0`;
    ownerMemberRef.current = null;
    agentMemberRef.current = null;
    channelRef.current = null;
    prevReadyRef.current = false;
    setTick((v) => v + 1);

    if (!client || !ownerValid || agentMxid === null) return;

    let alive = true;
    const unsubs: Array<() => void> = [];
    const update = () => {
      if (!alive) return;
      applyReadiness();
    };

    unsubs.push(
      client.bindings.subscribeRoomStateEvent<MemberContent>(
        roomId,
        "m.room.member",
        userId as string,
        (content) => {
          ownerMemberRef.current = content;
          update();
        },
      ),
    );
    unsubs.push(
      client.bindings.subscribeRoomStateEvent<MemberContent>(
        roomId,
        "m.room.member",
        agentMxid,
        (content) => {
          agentMemberRef.current = content;
          update();
        },
      ),
    );
    unsubs.push(
      client.bindings.subscribeRoomStateEvent<ChannelContent>(
        roomId,
        CHANNEL_TYPE_EVENT,
        "",
        (content) => {
          channelRef.current = content;
          update();
        },
      ),
    );
    return () => {
      alive = false;
      for (const unsub of unsubs) unsub();
    };
  }, [client, roomId, userId, ownerValid, agentMxid, applyReadiness]);

  const scopeRef = useRef<OwnAgentScope | null>(null);
  const liveReady =
    ownerValid &&
    isJoin(ownerMemberRef.current) &&
    isJoin(agentMemberRef.current) &&
    channelAllowsAgents(channelRef.current);
  scopeRef.current = liveReady
    ? { ownerMxid: userId as string, roomId, scopeId: scopeIdRef.current }
    : null;

  const clientRef = useRef(client);
  useEffect(() => {
    clientRef.current = client;
  });

  const guard = useCallback((expected: OwnAgentScope) => {
    const c = clientRef.current;
    const cur = scopeRef.current;
    if (!c || !cur) return false;
    if (cur.scopeId !== expected.scopeId) return false;
    if (cur.ownerMxid !== expected.ownerMxid) return false;
    if (cur.roomId !== expected.roomId) return false;
    return c.bindings.getUserId() === expected.ownerMxid;
  }, []);

  return { ready: liveReady, agentMxid, handle, scope: scopeRef.current, guard };
}
